// FundingRow.tsx

import DocumentTypeCombobox from "@/components/custom/DocumentTypeCombobox";
import FileDropzone from "@/components/custom/FileDropzone";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import {
  convertToEuro,
  formatRateDateLabel,
} from "@/libs/amount";
import {
  AlertCircle,
  AlertTriangle,
  Edit2,
  Loader2,
  Lock,
  Paperclip,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddFunderDialog from "../AddFunderDialog";
import ContactAdd from "../ContactAdd";
import VersementCard from "../new_fundings_card/VersementCard";
import VersementTerrain from "../new_fundings_card/VersementTerrain";
import VirementPrevu from "./VirementPrevu";
import {
  fetchHexaRateRateToEuroAtDateDetailed,
  fetchHexaRateRateToEuroAtDate,
  readRealFundingContractDateRaw,
  readRealFundingDateUsesFallback,
  resolveCurrentRateInfoWithTodayApi,
} from "./realFundingHistoricalRate";

/* ===================== Types ===================== */

type Funder = {
  id: number;
  fullname: string;
  representants: Array<{
    id: number;
    firstname: string;
    lastname: string;
    email?: string;
    phone?: string;
  }>;
};

function toNumber(v: any): number {
  const n = Number(v ?? 0);
  return !n || Number.isNaN(n) ? 0 : n;
}

function parseInputNumber(v: any): number {
  const n = Number(
    String(v ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );
  return !n || Number.isNaN(n) ? 0 : n;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function readRealFundingConvertedAmount(funding: any): number | null {
  const amount = Number(
    funding?.paidAmountConverted ??
      funding?.paid_amount_converted ??
      funding?.paid_amountConverted ??
      funding?.paidamountconverted,
  );
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function readRealFundingRateToEuro(funding: any): number | null {
  const amountEur = Number(funding?.paidAmountEUR ?? funding?.paidAmount);
  if (!Number.isFinite(amountEur) || amountEur <= 0) return null;

  const amountConverted = readRealFundingConvertedAmount(funding);
  if (amountConverted) {
    const rate = amountEur / amountConverted;
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  return null;
}

function readCodeFromCurrencyLike(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const code = value.trim().toUpperCase();
    return code || null;
  }
  if (typeof value === "object") {
    const code = String(value?.code ?? value?.currencyCode ?? "")
      .trim()
      .toUpperCase();
    return code || null;
  }
  return null;
}

function readIdFromCurrencyLike(value: any): number | null {
  const raw =
    typeof value === "object"
      ? value?.id
      : typeof value === "number"
        ? value
        : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(
  funding: any,
  projectCurrency?: any,
): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding,
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const projectCurrencyId = readIdFromCurrencyLike(projectCurrency);
  const projectCurrencyCode = readCodeFromCurrencyLike(projectCurrency);
  if (
    directId &&
    projectCurrencyId &&
    directId === projectCurrencyId &&
    projectCurrencyCode
  ) {
    return projectCurrencyCode;
  }

  return null;
}

function isFundingInputInLocalCurrency(
  funding: any,
  projectCurrency?: any,
): boolean {
  const code = readFundingInputCurrencyCode(funding, projectCurrency);
  return !!code && code !== "EUR";
}

function parseIdLike(value: any): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const direct = Number(value);
    if (!Number.isNaN(direct) && direct > 0) return direct;
    const match = value.match(/\/(\d+)(?:\/)?$/);
    if (match?.[1]) return Number(match[1]);
  }
  if (typeof value === "object") {
    const id = parseIdLike((value as any).id);
    if (id) return id;
    const iri = parseIdLike((value as any)["@id"]);
    if (iri) return iri;
  }
  return null;
}

function readObtainedRealFundingId(row: any): number | null {
  return (
    parseIdLike(row?.realFundingId) ??
    parseIdLike(row?.real_funding_id) ??
    parseIdLike(row?.real_funding) ??
    parseIdLike(row?.realfunding) ??
    parseIdLike(row?.realFunding) ??
    null
  );
}

function normalizeCollectionLike(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const values = Object.values(value);
  if (!values.length) return [];

  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object",
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function normalizeIdCollectionLike(value: any): number[] {
  const asArray = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.values(value)
      : [];

  if (!asArray.length) return [];

  const ids = asArray
    .map((item) => parseIdLike(item))
    .filter((id): id is number => id !== null && id > 0);
  return Array.from(new Set(ids));
}

function resolveAllocationRowsForFunding(
  fundingLike: any,
  projectLike: any,
): any[] {
  const fromFundingRaw =
    fundingLike?.obtainedFundings ??
    fundingLike?.obtainedFunding ??
    fundingLike?.obtained_fundings ??
    fundingLike?.obtained_funding;

  const fromFunding = normalizeCollectionLike(fromFundingRaw);
  if (fromFunding.length > 0) {
    return fromFunding;
  }

  const source = normalizeCollectionLike(
    projectLike?.obtainedFunding ?? projectLike?.obtained_funding,
  );
  if (source.length === 0) {
    return [];
  }

  const obtainedIds = normalizeIdCollectionLike(fromFundingRaw);
  if (obtainedIds.length > 0) {
    const idSet = new Set(obtainedIds);
    const linkedByObtainedIds = source.filter((of: any) =>
      idSet.has(Number(of?.id)),
    );
    if (linkedByObtainedIds.length > 0) {
      return linkedByObtainedIds;
    }
  }

  const fundingId = Number(fundingLike?.id);
  const withLinkedId = source.filter((of: any) => {
    const linkedId = readObtainedRealFundingId(of);
    return linkedId !== null && Number(linkedId) === fundingId;
  });
  if (withLinkedId.length > 0) {
    return withLinkedId;
  }

  const sourceHasNoLinkInfo =
    source.length > 0 &&
    source.every((of: any) => readObtainedRealFundingId(of) === null);
  const hasSingleContract =
    Array.isArray(projectLike?.realFundings) &&
    projectLike.realFundings.length === 1;
  if (sourceHasNoLinkInfo && hasSingleContract) {
    return source;
  }

  return [];
}

function readDesiredCurrencyLike(df: any, projectCurrency: any) {
  const raw =
    df?.currency ??
    df?.devise ??
    df?.currencySelected ??
    df?.deviseSelected ??
    df?.estimatedDevise ??
    df?.paidDevise ??
    df?.deviseAmount ??
    df?.currencyAmount ??
    null;

  if (raw && typeof raw === "object") return raw;

  if (typeof raw === "string" && raw.trim()) {
    const code = raw.trim().toUpperCase();
    if (projectCurrency?.code?.toUpperCase() === code) return projectCurrency;
    return { code };
  }

  if (typeof raw === "number") {
    if (projectCurrency?.id && Number(projectCurrency.id) === Number(raw)) {
      return projectCurrency;
    }
    if (Number(raw) === 3) return { code: "EUR" };
    return null;
  }

  return null;
}

async function desiredFundingAmountToEur(
  desiredFunding: any,
  projectCurrency: any,
  contractRateToEuro?: number | null,
): Promise<number> {
  const amountRaw = toNumber(
    desiredFunding?.amount ??
      desiredFunding?.value ??
      desiredFunding?.montant ??
      desiredFunding?.total ??
      desiredFunding?.totalAmount ??
      desiredFunding?.neededAmount ??
      0,
  );
  if (!amountRaw) return 0;

  const currency = readDesiredCurrencyLike(desiredFunding, projectCurrency);
  if (!currency) return amountRaw;

  const code = String(currency?.code ?? "EUR").toUpperCase();
  if (code === "EUR") return amountRaw;

  const projectCurrencyCode = String(projectCurrency?.code ?? "").toUpperCase();
  if (
    Number.isFinite(contractRateToEuro) &&
    Number(contractRateToEuro) > 0 &&
    projectCurrencyCode &&
    code === projectCurrencyCode
  ) {
    return toNumber(amountRaw * Number(contractRateToEuro));
  }

  try {
    const eur = await Promise.resolve(convertToEuro(amountRaw, currency));
    return toNumber(eur);
  } catch {
    return amountRaw;
  }
}

function desiredFundingLabel(df: any) {
  return (
    df?.title ??
    df?.name ??
    df?.financier?.fullname ??
    df?.financer?.fullname ??
    `Financement souhaité #${df?.id ?? "?"}`
  );
}

function buildCappedAllocations(
  ids: number[],
  total: number,
  maxById: Record<number, number>,
) {
  const result: Record<number, string> = {};
  if (!ids.length || total <= 0) return result;

  const totalRounded = round2(total);
  let remainingTotal = totalRounded;

  const items = ids.map((id) => {
    const raw = maxById[id];
    const cap =
      typeof raw === "number" && !Number.isNaN(raw) && raw > 0
        ? round2(raw)
        : Number.POSITIVE_INFINITY;
    return { id, cap };
  });

  let remaining = [...items];
  while (remaining.length > 0 && remainingTotal > 0) {
    const n = remaining.length;
    const equalShare = round2(remainingTotal / n);

    let cappedCount = 0;
    const nextRemaining: Array<{ id: number; cap: number }> = [];

    for (const item of remaining) {
      if (equalShare > item.cap) {
        const value = round2(item.cap);
        result[item.id] = value.toFixed(2);
        remainingTotal = round2(remainingTotal - value);
        cappedCount += 1;
      } else {
        nextRemaining.push(item);
      }
    }

    if (cappedCount === 0) {
      for (let i = 0; i < nextRemaining.length; i += 1) {
        const item = nextRemaining[i];
        const isLast = i === nextRemaining.length - 1;
        let value = isLast ? round2(remainingTotal) : equalShare;
        if (value > item.cap) value = round2(item.cap);
        result[item.id] = value.toFixed(2);
        remainingTotal = round2(remainingTotal - value);
      }
      break;
    }

    remaining = nextRemaining;
  }

  for (const id of ids) {
    if (!(id in result)) result[id] = "0.00";
  }

  return result;
}

function readContractAmountEur(
  funding: any,
  contractRateToEuro?: number | null,
  projectCurrency?: any,
): number {
  const shouldUseConvertedAmount =
    isFundingInputInLocalCurrency(funding, projectCurrency);
  const amountConverted = readRealFundingConvertedAmount(funding);
  const explicitRate = Number(contractRateToEuro);
  const fallbackStoredRate = readRealFundingRateToEuro(funding);
  const effectiveRate =
    Number.isFinite(explicitRate) && explicitRate > 0
      ? explicitRate
      : Number.isFinite(fallbackStoredRate) && Number(fallbackStoredRate) > 0
        ? Number(fallbackStoredRate)
        : null;

  if (shouldUseConvertedAmount && amountConverted && effectiveRate) {
    return round2(amountConverted * effectiveRate);
  }

  const hasContractAmount =
    funding?.paidAmountEUR !== null && funding?.paidAmountEUR !== undefined
      ? true
      : funding?.paidAmount !== null && funding?.paidAmount !== undefined;
  if (!hasContractAmount) return 0;
  return toNumber(funding?.paidAmountEUR ?? funding?.paidAmount);
}

function convertAllocationRowToEurAtContractRate(
  row: any,
  projectCurrency: any,
  contractRateToEuro?: number | null,
): number {
  const amountRaw = toNumber(
    row?.amount ??
      row?.value ??
      row?.montant ??
      row?.total ??
      row?.totalAmount ??
      0,
  );
  const convertedStored = toNumber(
    row?.convertedAmount ?? row?.converted_amount,
  );

  if (!amountRaw && convertedStored) return convertedStored;
  if (!amountRaw) return 0;

  const currency = readDesiredCurrencyLike(row, projectCurrency);
  const code = String(currency?.code ?? "EUR").toUpperCase();

  if (code === "EUR") {
    return amountRaw;
  }

  const projectCurrencyCode = String(projectCurrency?.code ?? "").toUpperCase();
  if (
    projectCurrencyCode &&
    code === projectCurrencyCode &&
    Number.isFinite(contractRateToEuro) &&
    Number(contractRateToEuro) > 0
  ) {
    return round2(amountRaw * Number(contractRateToEuro));
  }

  return convertedStored || amountRaw;
}

function normalizeDateForRateApi(value: any): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return match[1];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

type VerdeoFsAllocationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: any;
  funding: any;
  onUpdated: (apiRes: any) => void;
};

function VerdeoFsAllocationDialog({
  open,
  onOpenChange,
  project,
  funding,
  onUpdated,
}: VerdeoFsAllocationDialogProps) {
  const { postData } = usePostData<any>();
  const projectCurrency = project?.country?.currency;

  const desiredFundings = useMemo(
    () =>
      Array.isArray(project?.desiredFunding) ? project.desiredFunding : [],
    [project?.desiredFunding],
  );

  const projectName = useMemo(() => {
    const raw = project?.name ?? project?.title ?? "";
    const normalized = String(raw || "").trim();
    if (normalized) return normalized;
    const projectId = Number(project?.id);
    return projectId && !Number.isNaN(projectId)
      ? `Projet #${projectId}`
      : "Projet";
  }, [project?.name, project?.title, project?.id]);
  const contractDateRaw = readRealFundingContractDateRaw(funding);
  const contractDateLabel = useMemo(() => {
    const raw = contractDateRaw;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("fr-FR");
  }, [contractDateRaw]);
  const contractDateForRateApi = useMemo(
    () => normalizeDateForRateApi(contractDateRaw),
    [contractDateRaw],
  );

  const [step, setStep] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedDesiredFundingIds, setSelectedDesiredFundingIds] = useState<
    number[]
  >([]);
  const [loadingDesiredAmounts, setLoadingDesiredAmounts] = useState(false);
  const [desiredEurById, setDesiredEurById] = useState<Record<number, number>>(
    {},
  );
  const [desiredAllocations, setDesiredAllocations] = useState<
    Record<number, string>
  >({});
  const [contractDateRateToEuro, setContractDateRateToEuro] = useState<
    number | null
  >(null);
  const [contractDateRateStatus, setContractDateRateStatus] = useState<
    string | null
  >(null);
  const [loadingContractDateRate, setLoadingContractDateRate] = useState(false);
  const allocationSeedRef = useRef<string>("");
  const fundingInputIsLocalCurrency = useMemo(
    () => isFundingInputInLocalCurrency(funding, projectCurrency),
    [funding, projectCurrency?.id, projectCurrency?.code],
  );

  const contractRateToEuro = useMemo(() => {
    if (
      Number.isFinite(contractDateRateToEuro) &&
      Number(contractDateRateToEuro) > 0
    ) {
      return Number(contractDateRateToEuro);
    }
    const fallback = fundingInputIsLocalCurrency
      ? readRealFundingRateToEuro(funding)
      : null;
    if (Number.isFinite(fallback) && Number(fallback) > 0) {
      return Number(fallback);
    }
    return null;
  }, [
    contractDateRateToEuro,
    fundingInputIsLocalCurrency,
    funding?.paidAmountEUR,
    funding?.paidAmount,
    funding?.paidAmountConverted,
    funding?.paid_amount_converted,
  ]);
  const contractAmountEur = useMemo(
    () => readContractAmountEur(funding, contractRateToEuro, projectCurrency),
    [
      contractRateToEuro,
      projectCurrency?.id,
      projectCurrency?.code,
      funding?.paidAmountEUR,
      funding?.paidAmount,
      funding?.paidAmountConverted,
      funding?.paid_amount_converted,
    ],
  );

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSaving(false);
    setErrorMsg(null);
    setSelectedDesiredFundingIds([]);
    setDesiredAllocations({});
    allocationSeedRef.current = "";
  }, [open, funding?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open) {
        if (!cancelled) {
          setContractDateRateToEuro(null);
          setLoadingContractDateRate(false);
        }
        return;
      }

      const currencyCode = String(projectCurrency?.code ?? "").toUpperCase();
      if (!currencyCode || currencyCode === "EUR" || !contractDateForRateApi) {
        if (!cancelled) {
          setContractDateRateToEuro(currencyCode === "EUR" ? 1 : null);
          setLoadingContractDateRate(false);
        }
        return;
      }

      if (!cancelled) setLoadingContractDateRate(true);
      try {
        const url = `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${currencyCode}&date=${contractDateForRateApi}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Impossible de récupérer le taux historique.");
        }
        const data = await response.json();
        const entries = Array.isArray(data) ? data : [];
        const entry = (entries as any[]).find((e) => e.quote === currencyCode);
        const rate = Number(entry?.rate);
        const rateToEuro = Number.isFinite(rate) && rate > 0 ? 1 / rate : null;
        if (!cancelled) {
          setContractDateRateToEuro(rateToEuro);
        }
      } catch (err) {
        if (!cancelled) {
          setContractDateRateToEuro(null);
        }
        console.error("Erreur lors du fetch du taux à la date du contrat", err);
      } finally {
        if (!cancelled) {
          setLoadingContractDateRate(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, projectCurrency?.code, contractDateForRateApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open || !desiredFundings.length) {
        if (!cancelled) {
          setDesiredEurById({});
          setLoadingDesiredAmounts(false);
        }
        return;
      }
      if (!cancelled) setLoadingDesiredAmounts(true);
      const map: Record<number, number> = {};
      for (const df of desiredFundings) {
        const id = Number(df?.id);
        if (!id) continue;
        map[id] = await desiredFundingAmountToEur(
          df,
          projectCurrency,
          contractRateToEuro,
        );
      }
      if (!cancelled) {
        setDesiredEurById(map);
        setLoadingDesiredAmounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, desiredFundings, projectCurrency, contractRateToEuro]);

  const selectedTotalEur = useMemo(() => {
    if (!selectedDesiredFundingIds.length) return 0;
    return selectedDesiredFundingIds.reduce(
      (sum, id) => sum + toNumber(desiredEurById[id]),
      0,
    );
  }, [selectedDesiredFundingIds, desiredEurById]);

  const waitingDesiredAmounts =
    selectedDesiredFundingIds.length > 0 &&
    (loadingDesiredAmounts ||
      loadingContractDateRate ||
      selectedDesiredFundingIds.some(
        (id) => desiredEurById[Number(id)] === undefined,
      ));

  const needAllocationStep = selectedTotalEur - contractAmountEur > 0.0001;
  const step1UnallocatedAmount = useMemo(() => {
    if (needAllocationStep) return 0;
    return round2(contractAmountEur - selectedTotalEur);
  }, [needAllocationStep, contractAmountEur, selectedTotalEur]);
  const hasStep1UnallocatedAmount = step1UnallocatedAmount > 0.0001;
  const allDesiredFundingIds = useMemo(
    () =>
      desiredFundings
        .map((df: any) => Number(df?.id))
        .filter((id: number) => !!id && !Number.isNaN(id)),
    [desiredFundings],
  );
  const hasSelectedAllDesiredFundings = useMemo(() => {
    if (!allDesiredFundingIds.length) return false;
    const selectedSet = new Set(
      selectedDesiredFundingIds
        .map((id) => Number(id))
        .filter((id) => !!id && !Number.isNaN(id)),
    );
    return allDesiredFundingIds.every((id) => selectedSet.has(id));
  }, [allDesiredFundingIds, selectedDesiredFundingIds]);
  const canCreateIntermediateFunding =
    !needAllocationStep &&
    hasStep1UnallocatedAmount &&
    hasSelectedAllDesiredFundings;

  useEffect(() => {
    if (!open || !needAllocationStep || step !== 2) return;
    const ids = selectedDesiredFundingIds
      .map((n) => Number(n))
      .filter((n) => !!n && !Number.isNaN(n))
      .sort((a, b) => a - b);
    const capsKey = ids
      .map((id) =>
        desiredEurById[id] === undefined ? "x" : round2(desiredEurById[id]),
      )
      .join(",");
    const seed = `${ids.join(",")}|${round2(contractAmountEur)}|${capsKey}`;
    if (allocationSeedRef.current === seed) return;

    setDesiredAllocations(
      buildCappedAllocations(ids, contractAmountEur, desiredEurById),
    );
    allocationSeedRef.current = seed;
  }, [
    open,
    step,
    needAllocationStep,
    selectedDesiredFundingIds,
    desiredEurById,
    contractAmountEur,
  ]);

  const allocationTotal = useMemo(() => {
    if (!needAllocationStep) return 0;
    return selectedDesiredFundingIds.reduce((sum, id) => {
      const raw = desiredAllocations[Number(id)];
      return sum + parseInputNumber(raw);
    }, 0);
  }, [needAllocationStep, selectedDesiredFundingIds, desiredAllocations]);

  const allocationExceeds =
    needAllocationStep && allocationTotal - contractAmountEur > 0.0001;
  const allocationHasUnallocatedAmount =
    needAllocationStep && contractAmountEur - allocationTotal > 0.0001;
  const allocationRemaining = useMemo(() => {
    if (!needAllocationStep) return 0;
    return round2(contractAmountEur - allocationTotal);
  }, [needAllocationStep, contractAmountEur, allocationTotal]);

  const toggleDesiredFunding = (id: number) => {
    setSelectedDesiredFundingIds((prev) => {
      const setIds = new Set(prev.map((n) => Number(n)));
      if (setIds.has(Number(id))) setIds.delete(Number(id));
      else setIds.add(Number(id));
      return Array.from(setIds);
    });
  };

  const goToManualAllocationStep = () => {
    if (!needAllocationStep) return;
    setErrorMsg(null);
    setStep(2);
  };

  const goToNextStep = () => {
    if (contractAmountEur <= 0) {
      setErrorMsg("Montant du contrat introuvable.");
      return;
    }
    if (!selectedDesiredFundingIds.length) {
      setErrorMsg(
        "Sélectionnez au moins un financement souhaité pour continuer.",
      );
      return;
    }
    if (waitingDesiredAmounts) {
      setErrorMsg(
        "Chargement des montants des financements souhaités en cours. Réessayez dans un instant.",
      );
      return;
    }
    if (!needAllocationStep && hasStep1UnallocatedAmount) {
      setErrorMsg(
        `Le montant du contrat n'est pas totalement affecté. Il reste ${step1UnallocatedAmount.toLocaleString(
          "fr-FR",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )} € à affecter.${
          hasSelectedAllDesiredFundings
            ? " Cliquez sur le lien du message d'alerte pour poursuivre avec un financement intermédiaire."
            : ""
        }`,
      );
      return;
    }
    setErrorMsg(null);
    if (needAllocationStep) {
      void saveAllocation();
      return;
    }
    void saveAllocation();
  };

  const continueWithIntermediateFunding = () => {
    if (!canCreateIntermediateFunding || saving || waitingDesiredAmounts) {
      return;
    }

    const confirmed = window.confirm(
      `Le montant du contrat n'est pas totalement affecté. Il reste ${step1UnallocatedAmount.toLocaleString(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )} €.\n\nConfirmez-vous la poursuite ? Un financement intermédiaire sera créé avec ce montant restant.`,
    );
    if (!confirmed) return;

    void saveAllocation({ allowIntermediateFundingRemainder: true });
  };

  const saveAllocation = async (options?: {
    allowIntermediateFundingRemainder?: boolean;
  }) => {
    const allowIntermediateFundingRemainder =
      options?.allowIntermediateFundingRemainder === true;

    try {
      setErrorMsg(null);
      setSaving(true);

      if (!project?.id) {
        throw new Error("Projet introuvable.");
      }
      if (contractAmountEur <= 0) {
        throw new Error("Montant du contrat introuvable.");
      }

      const financierId =
        parseIdLike(funding?.financier) ?? parseIdLike(funding?.financer);
      if (!financierId) {
        throw new Error("Financeur introuvable sur ce contrat.");
      }

      if (!selectedDesiredFundingIds.length) {
        throw new Error("Sélectionnez au moins un financement souhaité.");
      }
      if (waitingDesiredAmounts) {
        throw new Error(
          "Chargement des montants des financements souhaités en cours.",
        );
      }

      const selectedIdsNormalized = selectedDesiredFundingIds
        .map((n) => Number(n))
        .filter((n) => !!n && !Number.isNaN(n));

      const effectiveDesiredAllocations =
        needAllocationStep && step !== 2
          ? buildCappedAllocations(
              selectedIdsNormalized,
              contractAmountEur,
              desiredEurById,
            )
          : desiredAllocations;

      const effectiveAllocationTotal = needAllocationStep
        ? selectedIdsNormalized.reduce((sum, id) => {
            const raw = effectiveDesiredAllocations[Number(id)];
            return sum + parseInputNumber(raw);
          }, 0)
        : 0;
      const effectiveAllocationExceeds =
        needAllocationStep &&
        effectiveAllocationTotal - contractAmountEur > 0.0001;
      const effectiveAllocationHasUnallocatedAmount =
        needAllocationStep &&
        contractAmountEur - effectiveAllocationTotal > 0.0001;

      if (needAllocationStep && effectiveAllocationExceeds) {
        throw new Error(
          "Le total alloué dépasse le montant du contrat. Ajustez la répartition.",
        );
      }
      if (needAllocationStep && effectiveAllocationHasUnallocatedAmount) {
        throw new Error(
          `Le montant du contrat n'est pas totalement affecté. Il reste ${round2(
            contractAmountEur - effectiveAllocationTotal,
          ).toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} € à affecter.`,
        );
      }
      if (!needAllocationStep && hasStep1UnallocatedAmount) {
        if (!(allowIntermediateFundingRemainder && hasSelectedAllDesiredFundings)) {
          throw new Error(
            `Le montant du contrat n'est pas totalement affecté. Il reste ${step1UnallocatedAmount.toLocaleString(
              "fr-FR",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )} € à affecter.${
              hasSelectedAllDesiredFundings
                ? " Cliquez sur le lien du message d'alerte pour poursuivre avec un financement intermédiaire."
                : ""
            }`,
          );
        }
      }

      let desiredFundingsPayload: Array<
        number | { id: number; amount: number }
      > = selectedDesiredFundingIds.map((n) => Number(n));

      if (needAllocationStep) {
        desiredFundingsPayload = selectedIdsNormalized
          .map((id) => {
            const amount = round2(
              parseInputNumber(effectiveDesiredAllocations[id]),
            );
            return { id: Number(id), amount };
          })
          .filter((item) => item.id > 0 && item.amount > 0);
      }

      if (!desiredFundingsPayload.length) {
        throw new Error("Aucune répartition valide à enregistrer.");
      }

      const payload: Record<string, any> = {
        toEdit: funding?.id,
        project: Number(project.id),
        financier: Number(financierId),
        desiredFundings: desiredFundingsPayload,
      };

      if (
        Number.isFinite(contractRateToEuro) &&
        Number(contractRateToEuro) > 0
      ) {
        payload.contractRateToEuro = Number(contractRateToEuro);
      }

      const representantId = parseIdLike(funding?.representant);
      if (representantId) {
        payload.representant = Number(representantId);
      }

      if (
        allowIntermediateFundingRemainder &&
        !needAllocationStep &&
        hasStep1UnallocatedAmount &&
        hasSelectedAllDesiredFundings
      ) {
        const firstDesiredSourceId = selectedIdsNormalized
          .map((id) => Number(id))
          .find((id) => id > 0);
        payload.createIntermediateFundingForRemainder = true;
        payload.intermediateFundingRemainderEur = round2(step1UnallocatedAmount);
        if (firstDesiredSourceId) {
          payload.intermediateFundingDesiredSourceId =
            Number(firstDesiredSourceId);
        }
      }

      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding/save?by=project&onlyFunding=true`,
        data: payload,
      });

      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        (res as any).success === false
      ) {
        throw new Error(
          (res as any)?.message ||
            "Échec de l'affectation des financements souhaités.",
        );
      }

      onUpdated(res);
      toast({ title: "Répartition du contrat enregistrée." });
      onOpenChange(false);
    } catch (e: any) {
      setErrorMsg(
        e?.message ||
          "Erreur lors de l'affectation des financements souhaités.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Répartition d&apos;un contrat Verdeo</DialogTitle>
          <DialogDescription>
            Ce contrat Verdeo doit être affecté à un ou plusieurs financements
            souhaités.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-emerald-50/60 p-3 text-sm">
          <div className="font-semibold text-emerald-900">Contrat Verdeo</div>
          <div className="mt-1 text-xs text-emerald-900/90">
            Projet: {projectName}
          </div>
          <div className="mt-0.5 text-xs text-emerald-900/90">
            Montant du contrat:{" "}
            <strong>
              {contractAmountEur.toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              €
            </strong>
            {contractDateLabel ? ` • signé le ${contractDateLabel}` : ""}
          </div>
          {loadingContractDateRate ? (
            <div className="mt-1 inline-flex items-center gap-2 text-xs text-emerald-900/90">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Chargement du taux de change à la date du contrat...
            </div>
          ) : contractRateToEuro &&
            String(projectCurrency?.code ?? "").toUpperCase() !== "EUR" ? (
            <div className="mt-1 inline-flex items-start gap-1.5 text-xs text-emerald-900/90">
              <Lock className="mt-[1px] h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span>
                Taux utilisé (date du contrat): 1{" "}
                {projectCurrency?.symbol || projectCurrency?.code || ""} ={" "}
                {contractRateToEuro.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}{" "}
                € • 1 € ={" "}
                {(1 / contractRateToEuro).toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}{" "}
                {projectCurrency?.symbol || projectCurrency?.code || ""}
                {` • en date du ${contractDateLabel || contractDateForRateApi || "date non disponible"}`}
              </span>
            </div>
          ) : null}
          <div className="mt-2 text-xs text-emerald-900/90">
            Quels sont les besoins couverts par ce contrat ?
          </div>
        </div>

        {desiredFundings.length === 0 ? (
          <div className="rounded-lg border bg-slate-50 p-3 text-sm text-slate-600">
            Aucun financement souhaité restant à couvrir sur ce projet.
          </div>
        ) : step === 1 ? (
          <div className="space-y-4">
            <div className="rounded-xl border bg-white/60 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    Quels sont les besoins couverts par ce contrat ?
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sélectionnez un ou plusieurs besoins. Si le total
                    sélectionné dépasse le montant du contrat, une répartition
                    automatique au prorata sera appliquée par défaut.
                  </p>
                </div>

                <div className="text-right shrink-0">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Montant du contrat (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {contractAmountEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              {waitingDesiredAmounts && (
                <div className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-xs text-slate-600">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Chargement des montants des financements souhaités...
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total des financements souhaités sélectionnés (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {selectedTotalEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>

                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Montant du contrat (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {contractAmountEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              {hasStep1UnallocatedAmount && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-red-700">
                    Le montant du contrat n&apos;est pas totalement affecté. Il
                    reste{" "}
                    <strong>
                      {step1UnallocatedAmount.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      €
                    </strong>{" "}
                    à affecter.
                    {canCreateIntermediateFunding ? (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="underline font-semibold hover:no-underline"
                          onClick={continueWithIntermediateFunding}
                          disabled={saving || waitingDesiredAmounts}
                        >
                          Cliquez ici si vous voulez quand même poursuivre
                        </button>
                        , un financement intermédiaire sera créé avec le montant
                        restant.
                      </>
                    ) : null}
                  </div>
                </div>
              )}
              {needAllocationStep && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-yellow-700">
                    Le montant du contrat ne couvre pas totalement les besoins
                    sélectionnés. Les montants seront alloués automatiquement au
                    prorata du montant du contrat. Si vous souhaitez les
                    modifier manuellement,{" "}
                    <button
                      type="button"
                      className="underline font-semibold hover:no-underline"
                      onClick={goToManualAllocationStep}
                      disabled={saving || waitingDesiredAmounts}
                    >
                      cliquez ici
                    </button>
                    .
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[340px] overflow-auto pr-1">
                {desiredFundings.map((df: any) => {
                  const id = Number(df?.id);
                  const selected = selectedDesiredFundingIds.includes(id);
                  const amountEur = toNumber(desiredEurById[id]);
                  return (
                    <button
                      key={id || desiredFundingLabel(df)}
                      type="button"
                      onClick={() => id && toggleDesiredFunding(id)}
                      className={[
                        "text-left rounded-xl border p-3 transition shadow-sm",
                        "bg-white hover:bg-slate-50",
                        selected
                          ? "border-slate-900 ring-1 ring-slate-900"
                          : "border-slate-200",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 break-words">
                            {desiredFundingLabel(df)}
                          </div>
                          {df?.comment ? (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              {df.comment}
                            </div>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-xs text-slate-700">
                          {selected ? "Sélectionné" : "Choisir"}
                        </div>
                      </div>

                      <div className="mt-3 flex items-end justify-between">
                        <div className="text-xs text-muted-foreground">
                          Montant du besoin (EUR)
                        </div>
                        <div className="text-sm font-semibold text-slate-900">
                          {amountEur.toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          €
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border bg-white/60 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    Répartition du contrat Verdeo
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Le total des besoins sélectionnés dépasse le montant du
                    contrat. Indiquez la part de ce contrat à affecter à chaque
                    besoin.
                  </p>
                </div>

                <div className="text-right shrink-0">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Montant du contrat (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {contractAmountEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total sélectionné (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {selectedTotalEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>

                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total alloué (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {allocationTotal.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>

                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Reste à répartir (EUR)
                  </div>
                  <div
                    className={[
                      "mt-1 text-sm font-semibold",
                      allocationRemaining < 0
                        ? "text-red-600"
                        : "text-slate-900",
                    ].join(" ")}
                  >
                    {allocationRemaining.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              {allocationExceeds && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-yellow-700">
                    Le total alloué dépasse le montant du contrat. Ajustez la
                    répartition pour continuer.
                  </div>
                </div>
              )}
              {allocationHasUnallocatedAmount && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-red-700">
                    Le montant du contrat n&apos;est pas totalement affecté. Il
                    reste{" "}
                    <strong>
                      {allocationRemaining.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      €
                    </strong>{" "}
                    à affecter.
                  </div>
                </div>
              )}

              <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
                {selectedDesiredFundingIds.map((id) => {
                  const fs = desiredFundings.find(
                    (df: any) => Number(df?.id) === Number(id),
                  );
                  const cap = toNumber(desiredEurById[id]);
                  return (
                    <div key={id} className="rounded-lg border bg-white p-3">
                      <div className="text-sm font-semibold text-slate-900 break-words">
                        {desiredFundingLabel(fs)}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Maximum allouable (montant du besoin):{" "}
                        {cap.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €
                      </div>
                      <div className="mt-2 w-full md:w-56">
                        <label className="text-xs text-slate-600">
                          Montant alloué (€)
                        </label>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                          value={desiredAllocations[id] ?? ""}
                          onChange={(e) =>
                            setDesiredAllocations((prev) => ({
                              ...prev,
                              [id]: e.target.value,
                            }))
                          }
                          disabled={saving}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          <div>
            {step === 2 && (
              <button
                type="button"
                className="px-3 py-2 rounded border text-sm"
                onClick={() => setStep(1)}
                disabled={saving}
              >
                Précédent
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-2 rounded border text-sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Fermer
            </button>
            {step === 1 ? (
              <button
                type="button"
                className="px-3 py-2 rounded bg-primary text-white text-sm disabled:opacity-60"
                onClick={goToNextStep}
                disabled={
                  saving ||
                  desiredFundings.length === 0 ||
                  waitingDesiredAmounts ||
                  (!needAllocationStep && hasStep1UnallocatedAmount)
                }
              >
                {needAllocationStep
                  ? "Valider (répartition auto)"
                  : "Valider l'affectation"}
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-primary text-white text-sm disabled:opacity-60"
                onClick={saveAllocation}
                disabled={
                  saving ||
                  waitingDesiredAmounts ||
                  allocationExceeds ||
                  allocationHasUnallocatedAmount ||
                  selectedDesiredFundingIds.length === 0
                }
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Valider la répartition
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== Composant Principal ===================== */

function FundingRow({
  funding,
  project,
  projectIndicators,
  loadingProjectIndicators = false,
  currencySelected,
  projectCurrencySymbol,
  funders,
  setFunders,
  onUpdated,
  onDeleted,
}: {
  funding: any;
  project: any;
  projectIndicators?: any[];
  loadingProjectIndicators?: boolean;
  currencySelected: string;
  projectCurrencySymbol: string;
  funders: Funder[];
  setFunders: React.Dispatch<React.SetStateAction<Funder[]>>;
  onUpdated: (apiRes: any) => void;
  onDeleted: (apiRes: any) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const projectCurrency = project?.country?.currency;
  const contractRate404Warning =
    "Impossible de récupérer le taux à la date du contrat. Le taux actuel est utilisé pour l'affichage.";
  const warningContractDateRaw = readRealFundingContractDateRaw(funding);
  const warningContractDateForRateApi = useMemo(
    () => normalizeDateForRateApi(warningContractDateRaw),
    [warningContractDateRaw],
  );
  const desiredFundings = useMemo(
    () =>
      Array.isArray(project?.desiredFunding) ? project.desiredFunding : [],
    [project?.desiredFunding],
  );
  const paymentMoveTargets = useMemo(() => {
    if (!Array.isArray(project?.realFundings)) return [];

    return project.realFundings
      .filter(
        (candidate: any) =>
          Number(candidate?.id ?? 0) > 0 &&
          Number(candidate?.id ?? 0) !== Number(funding?.id ?? 0),
      )
      .map((candidate: any) => {
        const financer = candidate?.financier ?? candidate?.financer ?? null;
        const financerLabel =
          financer?.fullname ??
          financer?.structureName ??
          financer?.name ??
          `Contrat #${candidate?.id ?? "-"}`;
        const contractDateRaw = readRealFundingContractDateRaw(candidate);
        const contractDateLabel = contractDateRaw
          ? new Date(contractDateRaw).toLocaleDateString("fr-FR")
          : "Sans date";
        const contractAmount = readContractAmountEur(
          candidate,
          readRealFundingRateToEuro(candidate),
          projectCurrency,
        );

        return {
          id: Number(candidate.id),
          label: `${financerLabel} • ${contractDateLabel} • ${Number(
            contractAmount,
          ).toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} €`,
        };
      });
  }, [project?.realFundings, funding?.id, projectCurrency]);

  const [warningContractDateRateToEuro, setWarningContractDateRateToEuro] =
    useState<number | null>(null);
  const warningFundingInputIsLocalCurrency = useMemo(
    () => isFundingInputInLocalCurrency(funding, projectCurrency),
    [funding, projectCurrency?.id, projectCurrency?.code],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const currencyCode = String(projectCurrency?.code ?? "").toUpperCase();
      if (
        !currencyCode ||
        currencyCode === "EUR" ||
        !warningContractDateForRateApi
      ) {
        if (!cancelled) {
          setWarningContractDateRateToEuro(currencyCode === "EUR" ? 1 : null);
        }
        return;
      }

      try {
        const rate = await fetchHexaRateRateToEuroAtDate(
          currencyCode,
          warningContractDateForRateApi,
        );
        if (!cancelled) {
          setWarningContractDateRateToEuro(
            Number.isFinite(rate) && (rate as number) > 0 ? Number(rate) : null,
          );
        }
      } catch {
        if (!cancelled) {
          setWarningContractDateRateToEuro(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectCurrency?.code, warningContractDateForRateApi]);

  const warningContractRateToEuro = useMemo(() => {
    if (
      Number.isFinite(warningContractDateRateToEuro) &&
      Number(warningContractDateRateToEuro) > 0
    ) {
      return Number(warningContractDateRateToEuro);
    }
    const fallback = warningFundingInputIsLocalCurrency
      ? readRealFundingRateToEuro(funding)
      : null;
    if (Number.isFinite(fallback) && Number(fallback) > 0)
      return Number(fallback);
    return null;
  }, [
    warningContractDateRateToEuro,
    warningFundingInputIsLocalCurrency,
    funding?.paidAmount,
    funding?.paidAmountEUR,
    funding?.paidAmountConverted,
    funding?.paid_amount_converted,
  ]);
  const contractAmountEur = useMemo(
    () =>
      readContractAmountEur(funding, warningContractRateToEuro, projectCurrency),
    [
      warningContractRateToEuro,
      projectCurrency?.id,
      projectCurrency?.code,
      funding?.paidAmountEUR,
      funding?.paidAmount,
      funding?.paidAmountConverted,
      funding?.paid_amount_converted,
    ],
  );
  const contractAllocationRows = useMemo(
    () => resolveAllocationRowsForFunding(funding, project),
    [
      project?.obtainedFunding,
      project?.obtained_funding,
      project?.realFundings,
      funding?.id,
      funding?.obtainedFundings,
      funding?.obtainedFunding,
      funding?.obtained_fundings,
      funding?.obtained_funding,
    ],
  );
  const allocatedContractAmountEur = useMemo(() => {
    console.log("contractAllocationRows", contractAllocationRows);
    return round2(
      contractAllocationRows.reduce((sum, row: any) => {
        return (
          sum +
          convertAllocationRowToEurAtContractRate(
            row,
            projectCurrency,
            warningContractRateToEuro,
          )
        );
      }, 0),
    );
  }, [contractAllocationRows, projectCurrency, warningContractRateToEuro]);
  const remainingContractAmountEur = useMemo(
    () => round2(contractAmountEur - allocatedContractAmountEur),
    [contractAmountEur, allocatedContractAmountEur],
  );
  const hasPendingContractAllocation =
    !!funding?.verdeo &&
    desiredFundings.length > 0 &&
    remainingContractAmountEur > 0.0001;

  const [isFsDialogOpen, setFsDialogOpen] = useState(false);
  const [isFsDialogAutoPromptDismissed, setFsDialogAutoPromptDismissed] =
    useState(false);

  useEffect(() => {
    setFsDialogAutoPromptDismissed(false);
  }, [funding?.id, hasPendingContractAllocation]);

  useEffect(() => {
    if (!hasPendingContractAllocation) return;
    if (isFsDialogOpen) return;
    if (isFsDialogAutoPromptDismissed) return;
    setFsDialogOpen(true);
  }, [
    hasPendingContractAllocation,
    isFsDialogOpen,
    isFsDialogAutoPromptDismissed,
  ]);

  const openFsMappingDialog = () => {
    setFsDialogAutoPromptDismissed(false);
    setFsDialogOpen(true);
  };

  const onFsDialogOpenChange = (nextOpen: boolean) => {
    setFsDialogOpen(nextOpen);
    if (!nextOpen) {
      setFsDialogAutoPromptDismissed(true);
    }
  };

  const onEnterEdit = () => {
    setIsEditing(true);
  };
  const onExitEdit = () => setIsEditing(false);

  return (
    <div className="rounded-2xl border bg-white/95 shadow-sm p-4 md:p-5 space-y-4">
      {/* ===================== HEADER CONTRAT (LECTURE / EDITION) ===================== */}
      {!isEditing ? (
        <ContractViewCard
          funding={funding}
          project={project}
          projectCurrency={projectCurrency}
          currencySelected={currencySelected}
          projectCurrencySymbol={projectCurrencySymbol}
          onEdit={onEnterEdit}
          onDeleted={onDeleted}
          onUpdated={onUpdated}
        />
      ) : (
        <ContractEditCard
          funding={funding}
          project={project}
          currencySelected={currencySelected}
          projectCurrencySymbol={projectCurrencySymbol}
          funders={funders}
          setFunders={setFunders}
          onCancel={onExitEdit}
          onSaved={(apiRes) => {
            onUpdated(apiRes);
            onExitEdit();
          }}
        />
      )}

      {/* ===================== SECTION VERSEMENTS (TOUJOURS OUVERTE) ===================== */}
      <div className="pt-4 border-t border-slate-200 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Versements</div>

        {!!funding?.verdeo && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              Affectation des financements souhaités obligatoire sur le contrat
              Verdeo
            </div>
            <div className="text-xs text-amber-800">
              Le montant du contrat doit être rattaché à des financements
              souhaités pour garantir la traçabilité des fonds.
            </div>

            {desiredFundings.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-white p-2 text-xs text-amber-800">
                Aucun financement souhaité restant à couvrir sur ce projet.
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-700">
                  <span>Montant du contrat</span>
                  <strong>
                    {contractAmountEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </strong>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-700">
                  <span>Total déjà affecté</span>
                  <strong>
                    {allocatedContractAmountEur.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </strong>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-700">
                  <span>Reste à affecter</span>
                  <strong
                    className={
                      hasPendingContractAllocation
                        ? "text-amber-800"
                        : "text-emerald-700"
                    }
                  >
                    {Math.max(0, remainingContractAmountEur).toLocaleString(
                      "fr-FR",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{" "}
                    €
                  </strong>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] ${
                      hasPendingContractAllocation
                        ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {hasPendingContractAllocation
                      ? "Répartition requise"
                      : "Contrat réparti"}
                  </span>
                  {hasPendingContractAllocation ? (
                    <button
                      type="button"
                      className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                      onClick={openFsMappingDialog}
                    >
                      Répartir le contrat
                    </button>
                  ) : (
                    <span className="text-[11px] text-slate-500">
                      Aucune action requise
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl bg-slate-50/80 p-3 md:p-4">
            <VersementCard
              currency={projectCurrency}
              currencySelected={
                currencySelected === "EUR" ? null : currencySelected
              }
              funding={funding}
              onChange={() => null}
              onApiList={(res) => onUpdated(res)}
              desiredFundings={[]}
              readonly={funding.verdeo}
              allowMove={true}
              moveTargets={paymentMoveTargets}
            />
          </div>
          <div className="rounded-xl bg-slate-50/80 p-3 md:p-4">
            <VersementTerrain
              currency={projectCurrency}
              currencySelected={
                currencySelected === "EUR" ? null : currencySelected
              }
              funding={funding}
              projectIndicators={projectIndicators}
              loadingProjectIndicators={loadingProjectIndicators}
              onChange={() => null}
              desiredFundings={[]}
              readonly={false}
            />
          </div>
        </div>
      </div>

      <VerdeoFsAllocationDialog
        open={isFsDialogOpen}
        onOpenChange={onFsDialogOpenChange}
        project={project}
        funding={funding}
        onUpdated={onUpdated}
      />
    </div>
  );
}

export default FundingRow;

/* ===================== Contrat : Lecture (carte) ===================== */

function ContractViewCard({
  funding,
  project,
  projectCurrency,
  currencySelected,
  projectCurrencySymbol,
  onEdit,
  onDeleted,
  onUpdated,
}: {
  funding: any;
  project: any;
  projectCurrency?: any;
  currencySelected: string;
  projectCurrencySymbol: string;
  onEdit: () => void;
  onDeleted: (apiRes: any) => void;
  onUpdated: (apiRes: any) => void;
}) {
  const [confirmDeleteFundingOpen, setConfirmDeleteFundingOpen] =
    useState(false);
  const [isDeletingFunding, setIsDeletingFunding] = useState(false);
  const [isAllocationOpen, setIsAllocationOpen] = useState(false);
  const [isSavingAllocation, setIsSavingAllocation] = useState(false);
  const [allocationError, setAllocationError] = useState<string | null>(null);
  const [allocationRows, setAllocationRows] = useState<any[]>([]);
  const [allocationInputs, setAllocationInputs] = useState<
    Record<number, string>
  >({});

  const { postData } = usePostData<any>();
  const [openVirementPrevu, setOpenVirementPrevu] = useState(false);
  const contractRate404Warning =
    "Impossible de récupérer le taux à la date du contrat. Le taux actuel est utilisé pour l'affichage.";
  const contractFinancer = funding?.financier ?? funding?.financer ?? null;
  const contractFinancerLabel =
    contractFinancer?.fullname ??
    contractFinancer?.structureName ??
    contractFinancer?.name ??
    "-";

  useEffect(() => {
    setAllocationRows(resolveAllocationRowsForFunding(funding, project));
  }, [
    project?.obtainedFunding,
    project?.obtained_funding,
    project?.realFundings,
    funding?.id,
    funding?.obtainedFundings,
    funding?.obtainedFunding,
    funding?.obtained_fundings,
    funding?.obtained_funding,
  ]);

  const handleDeleteFunding = async () => {
    try {
      setIsDeletingFunding(true);
      const res = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/real-funding/delete/${
          funding.id
        }?by=project&onlyFunding=true`,
        data: {},
      });
      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        (res as any).success === false
      ) {
        throw new Error(
          (res as any)?.message || "Échec de la suppression du financement.",
        );
      }
      onDeleted(res);
      toast({ title: "Financement supprimé." });
    } catch (e: any) {
      toast({ title: e?.message || "Erreur lors de la suppression." });
    } finally {
      setIsDeletingFunding(false);
      setConfirmDeleteFundingOpen(false);
    }
  };

  const fundingInputIsLocalCurrency = useMemo(
    () => isFundingInputInLocalCurrency(funding, projectCurrency),
    [funding, projectCurrency?.id, projectCurrency?.code],
  );
  const contractStoredRateToEuro = useMemo(
    () =>
      fundingInputIsLocalCurrency ? readRealFundingRateToEuro(funding) : null,
    [funding, fundingInputIsLocalCurrency],
  );
  const [contractDateRateToEuro, setContractDateRateToEuro] = useState<
    number | null
  >(null);
  const [contractDateRateStatus, setContractDateRateStatus] = useState<
    string | null
  >(null);
  const contractDateRaw = readRealFundingContractDateRaw(funding);
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const currencyCode = String(projectCurrency?.code ?? "").toUpperCase();
      if (!currencyCode || currencyCode === "EUR" || !contractDateRaw) {
        if (!cancelled) {
          setContractDateRateToEuro(currencyCode === "EUR" ? 1 : null);
          setContractDateRateStatus(currencyCode === "EUR" ? "eur" : null);
        }
        return;
      }

      const result = await fetchHexaRateRateToEuroAtDateDetailed(
        currencyCode,
        contractDateRaw,
      );
      if (!cancelled) {
        setContractDateRateToEuro(
          Number.isFinite(result.rateToEuro) && (result.rateToEuro as number) > 0
            ? Number(result.rateToEuro)
            : null,
        );
        setContractDateRateStatus(result.status);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectCurrency?.code, contractDateRaw]);

  const [currentRateInfo, setCurrentRateInfo] = useState<Awaited<
    ReturnType<typeof resolveCurrentRateInfoWithTodayApi>
  > | null>(null);
  useEffect(() => {
    let canceled = false;
    setCurrentRateInfo(null);

    (async () => {
      const resolved = await resolveCurrentRateInfoWithTodayApi(projectCurrency);
      if (!canceled) setCurrentRateInfo(resolved);
    })();

    return () => {
      canceled = true;
    };
  }, [
    projectCurrency?.id,
    projectCurrency?.code,
    projectCurrency?.symbol,
    projectCurrency?.exchangeRates?.[0]?.ratedAt,
  ]);
  const currentRateDateLabel = useMemo(
    () => formatRateDateLabel(currentRateInfo?.ratedAt),
    [currentRateInfo?.ratedAt],
  );
  const usesCurrentRateFallbackAfter404 =
    contractDateRateStatus === "http_404" &&
    Number.isFinite(Number(currentRateInfo?.rateToEuro ?? 0)) &&
    Number(currentRateInfo?.rateToEuro ?? 0) > 0;

  const contractRateToEuro = useMemo(() => {
    if (contractDateRateToEuro && Number.isFinite(contractDateRateToEuro)) {
      return contractDateRateToEuro;
    }
    if (usesCurrentRateFallbackAfter404) {
      const current = Number(currentRateInfo?.rateToEuro ?? 0);
      if (Number.isFinite(current) && current > 0) return current;
    }
    return contractStoredRateToEuro;
  }, [
    contractDateRateToEuro,
    usesCurrentRateFallbackAfter404,
    currentRateInfo?.rateToEuro,
    contractStoredRateToEuro,
  ]);
  const contractAmountEur = useMemo(
    () => readContractAmountEur(funding, contractRateToEuro, projectCurrency),
    [
      contractRateToEuro,
      projectCurrency?.id,
      projectCurrency?.code,
      funding?.paidAmountEUR,
      funding?.paidAmount,
      funding?.paidAmountConverted,
      funding?.paid_amount_converted,
    ],
  );
  const hasContractAmount = contractAmountEur > 0;
  const contractConvertedStored = readRealFundingConvertedAmount(funding);
  const contractAmountDisplay = useMemo(() => {
    if (!hasContractAmount) return null;
    if (currencySelected === "EUR" || projectCurrency?.code === "EUR") {
      return contractAmountEur;
    }

    if (usesCurrentRateFallbackAfter404 && contractRateToEuro) {
      return contractAmountEur / contractRateToEuro;
    }

    if (fundingInputIsLocalCurrency && contractConvertedStored) {
      return contractConvertedStored;
    }

    if (contractRateToEuro) {
      return contractAmountEur / contractRateToEuro;
    }

    if (typeof funding?.paidAmount === "number") {
      return Number(funding.paidAmount);
    }

    return contractAmountEur;
  }, [
    hasContractAmount,
    currencySelected,
    projectCurrency?.code,
    fundingInputIsLocalCurrency,
    contractAmountEur,
    contractRateToEuro,
    usesCurrentRateFallbackAfter404,
    contractConvertedStored,
    funding?.paidAmount,
  ]);
  const useLocalSymbol =
    currencySelected !== "EUR" &&
    projectCurrency?.code !== "EUR" &&
    (!!contractRateToEuro ||
      (!!fundingInputIsLocalCurrency && !!contractConvertedStored));
  const amountDisplay =
    contractAmountDisplay !== null
      ? `${contractAmountDisplay.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${useLocalSymbol ? projectCurrencySymbol : "€"}`
      : "-";
  console.log("funding", funding);
  const contractRateDateLabel = useMemo(
    () => formatRateDateLabel(contractDateRaw ?? null),
    [contractDateRaw],
  );
  const contractRateDisplay = useMemo(() => {
    if (
      !contractRateToEuro ||
      !Number.isFinite(contractRateToEuro) ||
      contractRateToEuro <= 0
    ) {
      return null;
    }
    if (
      !projectCurrency?.code ||
      String(projectCurrency.code).toUpperCase() === "EUR"
    ) {
      return null;
    }
    const symbol =
      projectCurrency?.symbol || projectCurrency?.code || "devise locale";
    const rateDate = usesCurrentRateFallbackAfter404
      ? currentRateDateLabel || "date non disponible"
      : contractRateDateLabel || "date non disponible";

    return `${usesCurrentRateFallbackAfter404 ? "Taux actuel utilisé car le taux à la date du contrat est introuvable" : "Taux à la date du contrat"}: 1 ${symbol} = ${contractRateToEuro.toLocaleString(
      "fr-FR",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      },
    )} € • 1 € = ${(1 / contractRateToEuro).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })} ${symbol} • au ${rateDate}`;
  }, [
    contractRateToEuro,
    contractRateDateLabel,
    currentRateDateLabel,
    projectCurrency?.code,
    projectCurrency?.symbol,
    usesCurrentRateFallbackAfter404,
  ]);

  const contractDateDisplay = useMemo(() => {
    const raw = readRealFundingContractDateRaw(funding);
    if (!raw) return "-";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("fr-FR");
  }, [funding]);
  const contractDateUsesFallback = useMemo(
    () => readRealFundingDateUsesFallback(funding),
    [funding],
  );
  const isVerdeoFunding =
    funding?.verdeo === true ||
    funding?.verdeo === 1 ||
    funding?.verdeo === "1" ||
    funding?.viaVerdeo === true ||
    funding?.viaVerdeo === 1 ||
    funding?.viaVerdeo === "1" ||
    funding?.isVerdeo === true ||
    funding?.isVerdeo === 1 ||
    funding?.isVerdeo === "1";
  const contractDateFallbackWarning = useMemo(
    () =>
      "La date réelle du contrat n'est pas encore renseignée. Le taux de change est actuellement bloqué avec cette date de référence. Merci de renseigner la vraie date de signature du contrat.",
    [],
  );
  const allocationRateToEuro = useMemo(() => {
    if (Number.isFinite(contractRateToEuro) && Number(contractRateToEuro) > 0) {
      return Number(contractRateToEuro);
    }
    const fallback = Number(currentRateInfo?.rateToEuro ?? 0);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
  }, [contractRateToEuro, currentRateInfo?.rateToEuro]);
  const getAllocationRowEur = useCallback(
    (row: any) =>
      convertAllocationRowToEurAtContractRate(
        row,
        projectCurrency,
        allocationRateToEuro,
      ),
    [projectCurrency, allocationRateToEuro],
  );

  const allocationTotalEur = useMemo(() => {
    return allocationRows.reduce((sum, row) => {
      const current = allocationInputs[Number(row?.id)];
      const value =
        current === undefined
          ? getAllocationRowEur(row)
          : parseInputNumber(current);
      return sum + value;
    }, 0);
  }, [allocationRows, allocationInputs, getAllocationRowEur]);

  const allocationExceeds = allocationTotalEur - contractAmountEur > 0.0001;
  const allocationHasNonPositive = allocationRows.some((row) => {
    const current = allocationInputs[Number(row?.id)];
    const value =
      current === undefined
        ? getAllocationRowEur(row)
        : parseInputNumber(current);
    return value <= 0;
  });

  const openAllocationDialog = () => {
    const rows = resolveAllocationRowsForFunding(funding, project);
    setAllocationRows(rows);

    const next: Record<number, string> = {};
    for (const row of rows) {
      const id = Number(row?.id);
      if (!id) continue;
      next[id] = round2(getAllocationRowEur(row)).toFixed(2);
    }
    setAllocationInputs(next);
    setAllocationError(null);
    setIsAllocationOpen(true);
  };

  const saveAllocation = async () => {
    try {
      setAllocationError(null);

      if (allocationExceeds) {
        setAllocationError(
          "Le total alloué dépasse le montant du contrat. Ajustez la répartition pour continuer.",
        );
        return;
      }
      if (allocationHasNonPositive) {
        setAllocationError("Chaque part doit être strictement positive.");
        return;
      }

      setIsSavingAllocation(true);
      const updatesById = new Map<number, any>();

      for (const row of allocationRows) {
        const id = Number(row?.id);
        if (!id) continue;

        const nextConvertedAmount = round2(
          parseInputNumber(allocationInputs[id]),
        );
        const currentConverted = getAllocationRowEur(row);
        const currentAmount = toNumber(row?.amount);
        const ratio =
          currentConverted > 0 ? currentAmount / currentConverted : 1;
        const nextAmount = round2(Math.max(0, nextConvertedAmount * ratio));

        const payload = {
          title: row?.title ?? "",
          amount: nextAmount,
          convertedAmount: nextConvertedAmount,
          devise: row?.devise?.id ?? null,
          financer:
            row?.financer?.id ??
            row?.financier?.id ??
            funding?.financier?.id ??
            funding?.financer?.id ??
            undefined,
          representant:
            row?.representant?.id ?? funding?.representant?.id ?? undefined,
          pjs: Array.isArray(row?.media)
            ? row.media
                .map((m: any) => Number(m?.id))
                .filter((n: number) => !!n)
            : [],
        };

        const res = await postData({
          url: `${import.meta.env.VITE_IP_API}/api/obtained_funding/edit/${id}`,
          data: payload,
        });

        if (
          !res ||
          (typeof res === "object" &&
            "success" in res &&
            (res as any).success === false)
        ) {
          throw new Error("Échec de la mise à jour de la répartition.");
        }

        updatesById.set(id, {
          ...row,
          amount: nextAmount,
          convertedAmount: nextConvertedAmount,
        });
      }

      const nextRows = allocationRows.map((row) => {
        const id = Number(row?.id);
        return updatesById.get(id) || row;
      });
      setAllocationRows(nextRows);

      if (Array.isArray(project?.obtainedFunding)) {
        project.obtainedFunding = project.obtainedFunding.map((row: any) => {
          const id = Number(row?.id);
          return updatesById.get(id) || row;
        });
      }

      toast({ title: "Répartition mise à jour." });
      setIsAllocationOpen(false);
    } catch (e: any) {
      setAllocationError(
        e?.message || "Erreur lors de la mise à jour de la répartition.",
      );
    } finally {
      setIsSavingAllocation(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        {/* Colonne gauche : financeur + représentant */}
        <div className="space-y-4">
          <div>
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Financeur
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {contractFinancerLabel}
            </div>
          </div>

          <div>
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Représentant
            </div>
            <div className="mt-1 text-sm text-slate-900">
              {funding.representant ? (
                <>
                  {funding.representant.firstname}{" "}
                  {funding.representant.lastname}
                </>
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>

        {/* Colonne droite : montant + date + PJ + actions */}
        <div className="flex w-full flex-col items-stretch gap-3 md:w-auto md:items-end">
          <div className="grid gap-3 text-right sm:grid-cols-2 md:grid-cols-1 md:w-auto">
            <div>
              <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                Montant du contrat
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{amountDisplay}</div>
              {contractRateDisplay && (
                <div className="mt-1 inline-flex items-start gap-1.5 text-[11px] text-slate-500">
                  <Lock className="mt-[1px] h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <span>{contractRateDisplay}</span>
                </div>
              )}
            </div>
            <div>
              <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                Date de signature
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-900">
                <span>{contractDateDisplay}</span>
                {contractDateDisplay !== "-" &&
                contractDateUsesFallback &&
                !isVerdeoFunding ? (
                  <TooltipProvider delayDuration={120}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex cursor-help"
                          aria-label={contractDateFallbackWarning}
                        >
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[320px] text-left text-[11px] leading-relaxed"
                      >
                        {contractDateFallbackWarning}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
            </div>
          </div>

          <div className="w-full md:w-auto">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500 text-right mb-1">
              Pièces jointes
            </div>
            {!funding.verdeo && funding.paidPjs?.length > 0 ? (
              <div className="flex flex-col gap-1 text-xs text-primary text-right">
                {funding.paidPjs.map((pj: any) => (
                  <a
                    key={pj.id}
                    href={import.meta.env.VITE_MEDIA_BASE + pj.uri}
                    className="hover:underline truncate"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {pj.name}
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-400 text-right">
                Aucune pièce jointe
              </div>
            )}
          </div>

          {/* Actions */}
          {!funding.verdeo && (
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                onClick={openAllocationDialog}
              >
                Voir la repartition de ce don
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                onClick={onEdit}
              >
                <Edit2 className="h-3.5 w-3.5" />
                Modifier
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                onClick={() => setConfirmDeleteFundingOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Popup suppression contrat */}
      <Dialog
        open={confirmDeleteFundingOpen}
        onOpenChange={(o) =>
          !isDeletingFunding && setConfirmDeleteFundingOpen(o)
        }
      >
        <DialogContent className="max-w-sm">
          <div className="space-y-3">
            <h3 className="font-semibold">Supprimer ce financement ?</h3>
            <p className="text-sm text-gray-600">
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded border text-sm"
                onClick={() => setConfirmDeleteFundingOpen(false)}
                disabled={isDeletingFunding}
              >
                Annuler
              </button>
              <button
                className="px-3 py-2 rounded bg-red-600 text-white text-sm disabled:opacity-60 inline-flex items-center gap-2"
                onClick={handleDeleteFunding}
                disabled={isDeletingFunding}
              >
                {isDeletingFunding && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Supprimer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAllocationOpen}
        onOpenChange={(o) => !isSavingAllocation && setIsAllocationOpen(o)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Répartition de ce don</DialogTitle>
            <DialogDescription>
              Ajustez le montant alloué sur chaque financement obtenu lié à ce
              contrat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border bg-slate-50 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Montant du contrat (EUR)</span>
                <span className="font-semibold text-slate-900">
                  {contractAmountEur.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-slate-600">Total alloué (EUR)</span>
                <span className="font-semibold text-slate-900">
                  {allocationTotalEur.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </span>
              </div>
              {contractRateDisplay ? (
                <div className="mt-1 inline-flex items-start gap-1.5 text-xs text-slate-700">
                  {usesCurrentRateFallbackAfter404 ? (
                    <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-orange-500" />
                  ) : (
                    <Lock className="mt-[1px] h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  )}
                  <span>{contractRateDisplay}</span>
                </div>
              ) : currentRateInfo ? (
                <div className="mt-1 inline-flex items-start gap-1.5 text-xs text-slate-600">
                  {usesCurrentRateFallbackAfter404 ? (
                    <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-orange-500" />
                  ) : null}
                  <span>
                    {usesCurrentRateFallbackAfter404
                      ? "Taux actuel utilisé car le taux à la date du contrat est introuvable"
                      : "Taux de change actuel (fallback)"}{" "}
                    : 1{" "}
                    {currentRateInfo.currencySymbol} ={" "}
                    {currentRateInfo.rateToEuro.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}{" "}
                    € • 1 € ={" "}
                    {(1 / currentRateInfo.rateToEuro).toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}{" "}
                    {currentRateInfo.currencySymbol}
                    {` • en date du ${currentRateDateLabel || "date non disponible"}`}
                  </span>
                </div>
              ) : null}
            </div>

            {allocationExceeds && (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                Le total alloué dépasse le montant du contrat.
              </div>
            )}

            {allocationRows.length === 0 ? (
              <div className="text-sm text-slate-500">
                Aucune répartition disponible pour ce contrat.
              </div>
            ) : (
              allocationRows.map((row) => {
                const id = Number(row?.id);
                const deviseLabel =
                  row?.devise?.symbol || row?.devise?.code || "";
                const currentAmount = toNumber(row?.amount);
                const currentConverted = getAllocationRowEur(row);

                return (
                  <div key={id} className="rounded-lg border p-3">
                    <div className="text-sm font-medium text-slate-900">
                      {row?.title || `Financement #${id || "?"}`}{" "}
                      <span className="text-slate-600">
                        (
                        {currentAmount.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {deviseLabel})
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Montant d&apos;origine :{" "}
                      {currentAmount.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {deviseLabel}
                      {"  "}(≈{" "}
                      {currentConverted.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      €)
                    </div>

                    <div className="mt-2 w-full md:w-56">
                      <label className="text-xs text-slate-600">
                        Montant alloué (€)
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                        value={allocationInputs[id] ?? ""}
                        onChange={(e) =>
                          setAllocationInputs((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }))
                        }
                        onBlur={() =>
                          setAllocationInputs((prev) => {
                            const raw = prev[id];
                            if (raw === undefined || raw === "") return prev;
                            const parsed = parseInputNumber(raw);
                            if (!Number.isFinite(parsed)) return prev;
                            return {
                              ...prev,
                              [id]: round2(parsed).toFixed(2),
                            };
                          })
                        }
                        disabled={isSavingAllocation}
                      />
                    </div>
                  </div>
                );
              })
            )}

            {allocationError && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {allocationError}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-3 py-2 rounded border text-sm"
              onClick={() => setIsAllocationOpen(false)}
              disabled={isSavingAllocation}
            >
              Annuler
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-primary text-white text-sm disabled:opacity-60"
              onClick={saveAllocation}
              disabled={
                isSavingAllocation ||
                allocationExceeds ||
                allocationHasNonPositive ||
                allocationRows.length === 0
              }
            >
              {isSavingAllocation && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Virements prévus (feature existante, toujours là si tu veux la réutiliser) */}
      <Dialog open={openVirementPrevu} onOpenChange={setOpenVirementPrevu}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl">Virements prévus</DialogTitle>
            <DialogDescription>
              Retrouver les virements prévus pour ce financement. Ajouter,
              modifier ou supprimer les virements.
            </DialogDescription>
          </DialogHeader>

          <VirementPrevu
            value={funding.realFundingExpectedPaids}
            realFunding={funding.id}
            currencySelected={currencySelected}
            projectCurrency={projectCurrency}
            projectCurrencySymbol={projectCurrencySymbol}
            title="Virement prévu"
            ensureOneRow={false}
            onApiList={(res) => onUpdated(res)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ===================== Contrat : Edition (carte) ===================== */

function ContractEditCard({
  funding,
  project,
  currencySelected,
  projectCurrencySymbol,
  funders,
  setFunders,
  onCancel,
  onSaved,
}: {
  funding: any;
  project: any;
  currencySelected: string;
  projectCurrencySymbol: string;
  funders: Funder[];
  setFunders: React.Dispatch<React.SetStateAction<Funder[]>>;
  onCancel: () => void;
  onSaved: (apiRes: any) => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const projectId = project?.id;
  const projectCurrency = project?.country?.currency;
  const projectCurrencyId: number | undefined = projectCurrency?.id;
  const projectCurrencyCode: string | undefined = projectCurrency?.code;
  const fundingInputIsLocalCurrency = useMemo(
    () => isFundingInputInLocalCurrency(funding, projectCurrency),
    [funding, projectCurrency?.id, projectCurrency?.code],
  );

  // Form
  const [financierId, setFinancierId] = useState<string>(
    funding?.financier?.id || funding?.financer?.id
      ? String(funding?.financier?.id ?? funding?.financer?.id)
      : "",
  );
  const [representantId, setRepresentantId] = useState<string>(
    funding?.representant?.id ? String(funding.representant.id) : "",
  );
  const [paidAmountInput, setPaidAmountInput] = useState<string>(
    (() => {
      const hasContractAmount =
        funding?.paidAmountEUR !== null && funding?.paidAmountEUR !== undefined
          ? true
          : funding?.paidAmount !== null && funding?.paidAmount !== undefined;
      if (!hasContractAmount) return "";

      const amountEur = toNumber(funding?.paidAmountEUR ?? funding?.paidAmount);
      if (currencySelected === "EUR" || projectCurrencyCode === "EUR") {
        return String(amountEur);
      }

      const convertedStored = readRealFundingConvertedAmount(funding);
      if (fundingInputIsLocalCurrency && convertedStored) {
        return String(convertedStored);
      }

      const contractRateToEuro = fundingInputIsLocalCurrency
        ? readRealFundingRateToEuro(funding)
        : null;
      if (contractRateToEuro) {
        return String(amountEur / contractRateToEuro);
      }

      return String(amountEur);
    })(),
  );
  const [dateAmountInput, setDateAmountInput] = useState<string>(() => {
    const rawDate = readRealFundingContractDateRaw(funding);
    const d = rawDate ? new Date(rawDate) : null;
    return d
      ? new Date(d.getTime() - d.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 10)
      : "";
  });

  // PJ contrat (UI objets + ids pour API)
  const [topPjIds, setTopPjIds] = useState<number[]>(
    (funding?.paidPjs || []).map((m: any) => Number(m.id)),
  );
  const [topPjFiles, setTopPjFiles] = useState<
    Array<{ id: number; name: string }>
  >(
    (funding?.paidPjs || []).map((m: any) => ({
      id: m.id,
      name: m.name ?? "Fichier",
    })),
  );

  // Upload PJ
  const [topPjOpen, setTopPjOpen] = useState(false);
  const [topPjType, setTopPjType] = useState<any>(16);
  const [topPjComment, setTopPjComment] = useState("");
  const [topPjFilesToUpload, setTopPjFilesToUpload] = useState<File[]>([]);
  const [isUploadingTop, setIsUploadingTop] = useState(false);

  // Ajout Funder / Representant
  const [openAddFunderModal, setOpenAddFunderModal] = useState(false);
  const [openAddRepresentantModal, setOpenAddRepresentantModal] =
    useState(false);

  const selectedFunder = useMemo(
    () =>
      financierId
        ? funders.find((f) => String(f.id) === financierId)
        : undefined,
    [funders, financierId],
  );
  const selectedRepresentants = selectedFunder?.representants?.length
    ? selectedFunder.representants
    : [];

  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();
  const { postData } = usePostData<any>();

  const uploadTopPj = async () => {
    if (topPjFilesToUpload.length === 0 || !topPjType) {
      toast({ title: "Veuillez choisir un type et au moins un fichier." });
      return;
    }
    try {
      setIsUploadingTop(true);
      const data = new FormData();
      data.append("type", "11");
      data.append("subType", topPjType);
      data.append("description", topPjComment);

      for (const file of topPjFilesToUpload) {
        let compressed: Blob | null = null;
        try {
          compressed = await handleCompressImage(file, 1024, 768, 0.9);
        } catch {
          compressed = null;
        }
        const toAppend = compressed
          ? new File([compressed], file.name, { type: compressed.type })
          : file;
        data.append("files[]", toAppend);
      }

      const newMedias: any = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (!Array.isArray(newMedias) && newMedias?.success === false) {
        toast({ title: newMedias.message ?? "Upload échoué", duration: 1500 });
        return;
      }

      const metas = (Array.isArray(newMedias) ? newMedias : []).map((m) => ({
        id: m.id,
        name: m.name ?? "Fichier",
      }));

      setTopPjFiles((prev) => [...prev, ...metas]);
      setTopPjIds((prev) => [...prev, ...metas.map((m) => m.id)]);

      setTopPjFilesToUpload([]);
      setTopPjType(16);
      setTopPjComment("");
      setTopPjOpen(false);
      toast({ title: "Pièce(s) jointe(s) ajoutée(s).", duration: 1200 });
    } finally {
      setIsUploadingTop(false);
    }
  };

  const removeUploadedTopPj = (id: number) => {
    if (isSaving || isUploadingTop) return;
    setTopPjFiles((prev) => prev.filter((p) => p.id !== id));
    setTopPjIds((prev) => prev.filter((pid) => Number(pid) !== Number(id)));
  };

  const handleSave = async () => {
    setErrorMsg(null);
    try {
      setIsSaving(true);

      if (!financierId) {
        setErrorMsg("Veuillez choisir un financeur.");
        return;
      }
      if (!projectId || !projectCurrencyId) {
        setErrorMsg("Projet ou devise du projet introuvable.");
        return;
      }
      const amountNumber = Number(
        (paidAmountInput || "").toString().replace(/\s/g, "").replace(",", "."),
      );
      if (!amountNumber || isNaN(amountNumber)) {
        setErrorMsg("Montant invalide.");
        return;
      }

      const contractDateForRate =
        dateAmountInput || readRealFundingContractDateRaw(funding);
      const requiresHistoricalRate =
        !!projectCurrencyCode && projectCurrencyCode !== "EUR";
      let contractRateToEuro: number | null = null;
      if (requiresHistoricalRate) {
        if (!contractDateForRate) {
          setErrorMsg(
            "Date de signature requise pour récupérer le taux de change du contrat.",
          );
          return;
        }

        contractRateToEuro = await fetchHexaRateRateToEuroAtDate(
          projectCurrencyCode,
          contractDateForRate,
        );
        if (!contractRateToEuro) {
          setErrorMsg(
            "Le taux de change historique de ce contrat est introuvable. Impossible de convertir le montant.",
          );
          return;
        }
      }

      // EUR pour API
      let amountInEuro = amountNumber;
      if (currencySelected !== "EUR") {
        if (!contractRateToEuro) {
          setErrorMsg(
            "Le taux de change historique de ce contrat est introuvable. Impossible de convertir le montant.",
          );
          return;
        }

        amountInEuro = Number(amountNumber * contractRateToEuro);
      }

      const payload = {
        toEdit: funding.id,
        project: projectId,
        financier: Number(financierId),
        representant: representantId ? Number(representantId) : undefined,
        estimatedAmount: "0",
        estimatedDate: new Date(),
        estimatedDevise: 3,
        paidAmount: amountInEuro, // EUR
        paidDevise: projectCurrencyId, // id devise projet
        dateAmount: dateAmountInput ? new Date(dateAmountInput) : null,
        ...(contractRateToEuro
          ? { contractRateToEuro: Number(contractRateToEuro) }
          : {}),
        pjs: topPjIds || [], // ARRAY OF IDS
      };

      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding/save?by=project&onlyFunding=true`,
        data: payload,
      });

      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        (res as any).success === false
      ) {
        throw new Error(
          (res as any)?.message || "Échec de la sauvegarde du financement.",
        );
      }

      onSaved(res);
      toast({ title: "Financement (contrat) mis à jour." });
    } catch (e: any) {
      setErrorMsg(e?.message || "Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Ligne 1 : Financeur / Représentant */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Financeur */}
          <div className="space-y-1">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Financeur
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={financierId}
                onValueChange={(val) => {
                  setFinancierId(val);
                  setRepresentantId("");
                }}
                disabled={isSaving || isUploadingTop}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choisir un financeur" />
                </SelectTrigger>
                <SelectContent>
                  {funders.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.fullname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* + Financeur */}
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs whitespace-nowrap"
                onClick={() => setOpenAddFunderModal(true)}
                disabled={isSaving || isUploadingTop}
                title="Ajouter un financeur"
              >
                +
              </button>
            </div>
          </div>

          {/* Représentant */}
          <div className="space-y-1">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Représentant
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={representantId}
                onValueChange={(val) => setRepresentantId(val)}
                disabled={
                  isSaving ||
                  isUploadingTop ||
                  !financierId ||
                  (selectedRepresentants?.length ?? 0) === 0
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      !financierId
                        ? "Choisir le financeur d'abord"
                        : (selectedRepresentants?.length ?? 0) === 0
                          ? "Aucun représentant"
                          : "Choisir un représentant"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {selectedRepresentants?.length
                    ? selectedRepresentants.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          {r.firstname} {r.lastname}
                        </SelectItem>
                      ))
                    : null}
                </SelectContent>
              </Select>

              {/* + Représentant */}
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs whitespace-nowrap disabled:opacity-60"
                onClick={() => setOpenAddRepresentantModal(true)}
                disabled={isSaving || isUploadingTop || !financierId}
                title={
                  financierId
                    ? "Ajouter un représentant"
                    : "Choisir un financeur d'abord"
                }
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Ligne 2 : Montant / Date */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Montant contrat */}
          <div className="space-y-1">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Montant du contrat
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs opacity-70">
                {currencySelected === "EUR" ? "€" : projectCurrencySymbol}
              </span>
              <input
                type="text"
                inputMode="decimal"
                className="w-full rounded border px-6 py-1.5 text-sm"
                placeholder="0,00"
                value={paidAmountInput}
                onChange={(e) => setPaidAmountInput(e.target.value)}
                disabled={isSaving || isUploadingTop}
              />
            </div>
          </div>

          {/* Date contrat */}
          <div className="space-y-1">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Date de signature
            </div>
            <input
              type="date"
              className="w-full rounded border px-2 py-1.5 text-sm"
              value={dateAmountInput}
              onChange={(e) => setDateAmountInput(e.target.value)}
              disabled={isSaving || isUploadingTop}
            />
          </div>
        </div>

        {/* Ligne 3 : PJ + actions */}
        <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-start">
          {/* PJ contrat */}
          <div className="space-y-2">
            <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
              Pièces jointes
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs"
                onClick={() => setTopPjOpen(true)}
                disabled={isSaving}
                title="Uploader une pièce jointe"
              >
                <Paperclip className="w-3.5 h-3.5" />
                PJ
              </button>
              {topPjIds.length > 0 && (
                <span className="text-xs text-gray-600">
                  {topPjIds.length} fichier(s)
                </span>
              )}
            </div>

            {topPjFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 max-w-[280px]">
                {topPjFiles.map((f) => (
                  <span
                    key={f.id}
                    className="inline-flex items-center gap-1 rounded border bg-gray-50 px-2 py-1 text-xs"
                    title={f.name}
                  >
                    <span className="max-w-[160px] truncate">{f.name}</span>
                    <button
                      type="button"
                      className="p-0.5 hover:opacity-80"
                      onClick={() => removeUploadedTopPj(f.id)}
                      disabled={isSaving || isUploadingTop}
                      aria-label={`Retirer ${f.name}`}
                      title="Retirer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-4 md:mt-6">
            <button
              type="button"
              className="px-3 py-1.5 rounded-full border text-xs"
              onClick={onCancel}
              disabled={isSaving || isUploadingTop}
            >
              Annuler
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
              onClick={handleSave}
              disabled={isSaving || isUploadingTop}
            >
              {(isSaving || isUploadingTop) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Enregistrer
            </button>
          </div>
        </div>

        {errorMsg && <p className="text-sm text-red-600 pt-1">{errorMsg}</p>}
      </div>

      {/* POPUP PJ CONTRAT */}
      <Dialog
        open={topPjOpen}
        onOpenChange={(o) => !isUploadingTop && setTopPjOpen(o)}
      >
        <DialogContent className="max-w-lg">
          <div className="space-y-4">
            <h3 className="text-base font-semibold">
              Ajouter une pièce jointe (contrat)
            </h3>

            <div>
              <label className="text-sm font-medium">Type de document</label>
              <DocumentTypeCombobox value={topPjType} onChange={setTopPjType} />
            </div>

            <div>
              <label className="text-sm font-medium">Commentaire</label>
              <textarea
                value={topPjComment}
                onChange={(e) => setTopPjComment(e.target.value)}
                className="w-full rounded border p-2 text-sm"
                placeholder="Ajouter un commentaire (facultatif)"
                disabled={isUploadingTop}
              />
            </div>

            <div className="space-y-2">
              <FileDropzone
                accept="PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG"
                onFileSelected={setTopPjFilesToUpload}
              />
              {topPjFilesToUpload?.length > 0 && (
                <ul className="max-h-28 space-y-1 overflow-auto text-xs text-gray-700">
                  {topPjFilesToUpload.map((f, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        className="rounded border px-1 py-0.5 hover:bg-gray-50"
                        onClick={() =>
                          setTopPjFilesToUpload((arr) =>
                            arr.filter((_, i) => i !== idx),
                          )
                        }
                        disabled={isUploadingTop}
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded border text-sm"
                onClick={() => !isUploadingTop && setTopPjOpen(false)}
                disabled={isUploadingTop}
              >
                Fermer
              </button>
              <button
                className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-60"
                onClick={uploadTopPj}
                disabled={isUploadingTop}
              >
                {isUploadingTop && <Loader2 className="w-4 h-4 animate-spin" />}
                Upload
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL: Ajouter un financeur */}
      <AddFunderDialog
        hasTrigger={false}
        open={openAddFunderModal}
        onHandleOpen={setOpenAddFunderModal}
        onSaveSuccess={(res: Funder) => {
          setFunders((prev) => [
            ...prev,
            { ...res, representants: res.representants ?? [] },
          ]);
          setFinancierId(String(res.id));
          setRepresentantId("");
          setOpenAddFunderModal(false);
        }}
      />

      {/* MODAL: Ajouter un représentant */}
      <ContactAdd
        open={openAddRepresentantModal}
        setOpen={setOpenAddRepresentantModal}
        financer={financierId ? Number(financierId) : undefined}
        handleCreate={(newRepresentant: {
          id: number;
          firstname: string;
          lastname: string;
        }) => {
          if (!financierId) {
            setOpenAddRepresentantModal(false);
            return;
          }
          const finId = Number(financierId);
          setFunders((prev) =>
            prev.map((f) =>
              f.id === finId
                ? {
                    ...f,
                    representants: [
                      ...(f.representants || []),
                      newRepresentant,
                    ],
                  }
                : f,
            ),
          );
          setOpenAddRepresentantModal(false);
          setRepresentantId(String(newRepresentant.id));
        }}
      />
    </>
  );
}
